import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { Logo } from "@/components/Logo";

const TikTokIcon = ({ size = 16, ...props }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" {...props}>
    <path d="M16.6 5.82c-1-.93-1.5-2.16-1.5-3.62h-3.1v13.87c0 1.62-1.32 2.94-2.94 2.94a2.94 2.94 0 0 1 0-5.88c.3 0 .59.05.86.13V9.99a6.05 6.05 0 0 0-.86-.06 6.05 6.05 0 1 0 6.05 6.05V8.62c1.15.82 2.55 1.3 4.06 1.3V6.83c-.9 0-1.79-.36-2.57-1.01Z" />
  </svg>
);

const LINKS = [
  { label: "Beranda", id: "hero" },
  { label: "Tentang", id: "tentang" },
  { label: "Produk", id: "produk" },
  { label: "Artikel", href: "/artikel" },
  { label: "Kontak", id: "pesan" },
];

const SECTION_IDS = LINKS.filter((l) => l.id).map((l) => l.id);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const SOCIALS = [
  { key: "instagram_url", label: "Instagram", Icon: Instagram },
  { key: "facebook_url", label: "Facebook", Icon: Facebook },
  { key: "tiktok_url", label: "TikTok", Icon: TikTokIcon },
];

export const Navbar = () => {
  const { settings, waLink } = useSettings();
  const socials = SOCIALS.filter((s) => settings[s.key]);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("hero");
  const isArticleRoute = location.pathname.startsWith("/artikel");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isArticleRoute) return;
    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isArticleRoute]);

  const handleNavClick = (id) => {
    setActiveId(id);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-50"
    >
      <div
        className={`mx-auto max-w-7xl px-6 md:px-10 mt-4 flex items-center justify-between rounded-full transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-cream/70 backdrop-blur-xl shadow-[0_8px_32px_rgba(217,119,6,0.10)] py-3"
            : "bg-transparent py-4"
        }`}
      >
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo("hero")}
          className="font-brand font-bold text-xl tracking-tight flex items-center gap-2 text-ink"
        >
          <Logo src={settings.logo_url} size={32} />
          {settings.brand_name}
        </button>

        <nav className="hidden md:flex items-center gap-1 rounded-full bg-cream-200/80 p-1.5">
          {LINKS.map((l) => {
            const isActive = l.href ? isArticleRoute : !isArticleRoute && activeId === l.id;
            const cls = `px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${
              isActive ? "bg-honey text-ink" : "text-stone2 hover:text-ink"
            }`;
            return l.href ? (
              <Link key={l.href} to={l.href} data-testid={`nav-${l.href}`} className={cls}>
                {l.label}
              </Link>
            ) : (
              <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => handleNavClick(l.id)} className={cls}>
                {l.label}
              </button>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {socials.map((s) => (
            <a
              key={s.key}
              href={settings[s.key]}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              className="flex h-9 w-9 items-center justify-center rounded-full text-stone2 hover:text-ink hover:bg-cream-200 transition-colors duration-300"
            >
              <s.Icon size={17} />
            </a>
          ))}
          <a
            data-testid="nav-whatsapp"
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-honey hover:text-ink transition-colors duration-300"
          >
            WhatsApp
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-ink"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-6 mt-2 rounded-3xl bg-cream/95 backdrop-blur-xl p-6 shadow-xl border border-border/40"
          >
            {LINKS.map((l) => {
              const isActive = l.href ? isArticleRoute : !isArticleRoute && activeId === l.id;
              const cls = `block w-full text-left py-3 text-lg font-medium border-b border-border/40 last:border-0 ${
                isActive ? "text-honey-dark" : "text-ink"
              }`;
              return l.href ? (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`nav-mobile-${l.href}`}
                  onClick={() => setOpen(false)}
                  className={cls}
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => {
                    handleNavClick(l.id);
                    setOpen(false);
                  }}
                  className={cls}
                >
                  {l.label}
                </button>
              );
            })}
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex w-full justify-center rounded-full bg-honey px-5 py-3 font-semibold text-ink"
            >
              Hubungi via WhatsApp
            </a>
            {socials.length > 0 && (
              <div className="mt-4 flex items-center justify-center gap-4">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={settings[s.key]}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border/40 text-stone2 hover:text-ink hover:bg-cream-200 transition-colors duration-300"
                  >
                    <s.Icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
